import { type FC, type ReactNode, type ComponentProps } from 'react';

import {
    InternalScrollCarouselProvider,
    useInternalScrollCarousel,
} from '../helpers/InternalScrollCarouselContext';
import { Arrow } from '../Arrow/Arrow';
import { ScrollContainer } from '../ScrollContainer/ScrollContainer';

import { type OnArrowClick } from '../types';

import styles from './InnerScrollCarousel.scss';

const Inner: FC<{
    children: ReactNode;
    showArrows: boolean;
    onArrowClick?: OnArrowClick;
    dataTn?: string;
    noGap?: boolean;
}> = ({ children, showArrows, onArrowClick, dataTn, noGap }) => {
    const { carouselOrientation } = useInternalScrollCarousel();

    return (
        <div
            className={`${styles.wrapper} ${styles[carouselOrientation]} ${
                noGap ? styles.noGap : ''
            }`}
            data-tn={dataTn}
        >
            {showArrows && <Arrow type="previous" onArrowClick={onArrowClick} noInnerSpacing />}

            <ScrollContainer>{children}</ScrollContainer>

            {showArrows && <Arrow type="next" onArrowClick={onArrowClick} noInnerSpacing />}
        </div>
    );
};

export const InnerScrollCarousel: FC<
    {
        children: ReactNode;
        showArrows?: boolean;
        onArrowClick?: OnArrowClick;
        dataTn?: string;
        noGap?: boolean;
        snapAlign?: 'start' | 'center';
        shouldAdjustFlexBasis?: boolean;
    } & ComponentProps<typeof InternalScrollCarouselProvider>
> = ({
    children,
    showArrows = false,
    onArrowClick,
    dataTn,
    noGap,
    snapAlign = 'start',
    shouldAdjustFlexBasis,
    ...props
}) => {
    return (
        <InternalScrollCarouselProvider
            {...props}
            snapAlign={snapAlign}
            shouldAdjustFlexBasis={shouldAdjustFlexBasis}
        >
            <Inner
                showArrows={showArrows}
                onArrowClick={onArrowClick}
                dataTn={dataTn}
                noGap={noGap}
            >
                {children}
            </Inner>
        </InternalScrollCarouselProvider>
    );
};
